/* Created by Interactive 63 - https://interactive63.com */
/**
 * The SVG import pipeline: size check, parse, copy design-tool labels, sanitise, clean up,
 * prepare, and build a new project. Returns either a project or a user-facing error message.
 * The clean-up step is passed in so the editor can load the optimiser only when it is needed.
 */
import { createEmptyProject } from '../model/defaults';
import type { Project } from '../model/types';
import { prepareSvg } from './prepare';
import { simplifyDocument } from './simplify';
import { sanitiseSvg } from './sanitise';

export type ImportResult =
  { ok: true; project: Project; warnings: string[] } | { ok: false; error: string };

export interface ImportOptions {
  /** Run the conservative SVGO clean-up. */
  clean: boolean;
  /** Split compound paths into separate parts, remove single-child groups and flatten transforms. */
  simplify?: boolean;
}

const MAX_BYTES = 5 * 1024 * 1024;
const SMIL_SELECTOR = 'animate, animateMotion, animateTransform, set';
const INVALID_MESSAGE =
  "This file isn't a valid SVG. If it opens in a browser, try re-exporting it from your design tool.";

/** Parses markup as XML, returning null when it is not a well-formed SVG document. */
function parseSvg(text: string): Document | null {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return null;
  return doc.documentElement.localName === 'svg' ? doc : null;
}

/** Derives a project name from a file name, dropping the extension. */
function projectName(fileName: string): string {
  const base = fileName.replace(/\.[^.]*$/, '').trim();
  return base === '' ? 'Pasted SVG' : base;
}

/** Clean-up applied to sanitised markup before it is prepared. */
export type MarkupCleaner = (markup: string) => string;

/** Imports SVG text into a new project, tidying the sanitised markup with the given cleaner. */
export function importSvgWith(
  text: string,
  fileName: string,
  clean: MarkupCleaner,
  options: { simplify?: boolean } = {},
): ImportResult {
  if (text.length > MAX_BYTES) {
    return {
      ok: false,
      error: 'This SVG is larger than 5 MB. Try optimising it first, for example with SVGOMG.',
    };
  }

  const parsed = parseSvg(text);
  if (!parsed) return { ok: false, error: INVALID_MESSAGE };

  const warnings: string[] = [];
  if (parsed.querySelector(SMIL_SELECTOR)) {
    warnings.push(
      "This SVG contained built-in SMIL animations. They have been removed so SVG Tweener's animations work predictably.",
    );
  }

  // Namespaced editor labels are stripped by later steps, so keep them in a plain attribute.
  for (const el of Array.from(parsed.getElementsByTagName('*'))) {
    const label = el.getAttribute('inkscape:label');
    if (label) el.setAttribute('data-st-label', label);
  }

  const sanitised = sanitiseSvg(new XMLSerializer().serializeToString(parsed.documentElement));
  if (sanitised === '') return { ok: false, error: INVALID_MESSAGE };
  const cleaned = clean(sanitised);

  const doc = parseSvg(cleaned);
  if (!doc) return { ok: false, error: INVALID_MESSAGE };
  if (options.simplify) warnings.push(...simplifyDocument(doc));
  const prepared = prepareSvg(doc);

  const project = createEmptyProject(projectName(fileName));
  project.svg = {
    source: prepared.source,
    viewBox: prepared.viewBox,
    fileName,
    ...(prepared.originalWidth === undefined ? {} : { originalWidth: prepared.originalWidth }),
    ...(prepared.originalHeight === undefined ? {} : { originalHeight: prepared.originalHeight }),
  };
  project.elements = prepared.elements;
  project.rootIds = prepared.rootIds;
  return { ok: true, project, warnings: [...warnings, ...prepared.warnings] };
}
